/**
 * DATORACODE - Shop the Look Slider (PDP)
 *
 * Lazy-loads complementary products via IntersectionObserver,
 * initializes Flickity carousel, handles color-swap on variant change,
 * and wires up the existing upsell modal.
 */
(function () {
  'use strict';

  // ─── 1. Init: find all slider containers and observe ────────
  function init() {
    var containers = document.querySelectorAll('.shop-the-look-slider');
    if (!containers.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        loadSlider(entry.target);
      });
    }, { rootMargin: '200px' });

    containers.forEach(function (el) {
      observer.observe(el);
    });

    // Listen for variant changes on the PDP (registered once)
    document.addEventListener('variant:changed', function() {
      document.querySelectorAll('.shop-the-look-slider').forEach(function(container) {
        updateAllCards(container);
      });
    });
  }

  // ─── 2. Fetch recommendations and inject HTML ──────────────
  function loadSlider(container) {
    var url = container.getAttribute('data-url');
    if (!url) return;

    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        // Extract only the inner HTML of the section
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var sectionInner = doc.querySelector('.shopify-section');
        if (!sectionInner || !sectionInner.innerHTML.trim()) return;

        container.innerHTML = sectionInner.innerHTML;

        // Set heading from data attribute
        var headingEl = container.querySelector('[data-stl-heading]');
        var headingText = container.getAttribute('data-heading');
        if (headingEl && headingText) {
          headingEl.textContent = headingText;
        }

        // Apply current color to all cards
        updateAllCards(container);

        // Init Flickity
        initFlickity(container);

      });
  }

  // ─── 3. Read current PDP color ─────────────────────────────
  function getCurrentColor() {
    var checkedInput = document.querySelector(
      'variant-picker input[data-option-position="1"]:checked'
    );
    if (checkedInput) {
      var label = document.querySelector('label[for="' + checkedInput.id + '"]');
      if (label) {
        var text = label.querySelector('.visually-hidden');
        if (text) return text.textContent.trim();
      }
    }
    return null;
  }

  // ─── 4. Color taxonomy matching ─────────────────────────────
  var byName = null;
  var byTaxonomy = null;

  function initColorTaxonomy(container) {
    if (byName) return; // bereits initialisiert
    try {
      byName = JSON.parse(container.getAttribute('data-color-taxonomy'));
    } catch (e) {
      byName = {};
    }
    byTaxonomy = {};
    Object.keys(byName).forEach(function (name) {
      var tax = byName[name];
      if (!byTaxonomy[tax]) byTaxonomy[tax] = [];
      byTaxonomy[tax].push(name);
    });
  }

  function findColorEntry(colorMap, color) {
    if (!color) return colorMap['default'];

    // 1. Exakter Match
    if (colorMap[color]) return colorMap[color];

    // 2. Taxonomy-Fallback: Synonyme suchen
    var tax = byName && byName[color];
    if (tax && byTaxonomy[tax]) {
      for (var i = 0; i < byTaxonomy[tax].length; i++) {
        if (colorMap[byTaxonomy[tax][i]]) return colorMap[byTaxonomy[tax][i]];
      }
    }

    // 3. Default
    return colorMap['default'];
  }

  // ─── 5. Update all cards to match current color ────────────
  function updateAllCards(container) {
    initColorTaxonomy(container);
    var color = getCurrentColor();
    var cards = container.querySelectorAll('.stl-card');

    cards.forEach(function (card) {
      var colorMap;
      try {
        colorMap = JSON.parse(card.getAttribute('data-color-map'));
      } catch (e) {
        return;
      }

      var entry = findColorEntry(colorMap, color);
      if (!entry) return;

      var img = card.querySelector('.stl-card__image');
      if (img) {
        img.src = entry.image_url;
      }

      var link = card.querySelector('[data-upsell-trigger]');
      if (link) {
        var separator = entry.product_url.includes('?') ? '&' : '?';
        link.setAttribute('data-upsell-href', entry.product_url + separator + 'variant=' + entry.variant_id);
      }
    });
  }

  // ─── 5. Flickity carousel ─────────────────────────────────
  async function initFlickity(container) {
    var carousel = container.querySelector('[data-stl-carousel]');
    if (!carousel) return;

    // Lazy-load Flickity if not yet available
    if (!window.ThemeFlickity && !window.Flickity) {
      if (typeof LibraryLoader !== 'undefined') {
        await LibraryLoader.load('flickity');
      } else {
        return;
      }
    }

    var FlickityClass = window.ThemeFlickity || window.Flickity;
    if (!FlickityClass) return;

    var flkty = new FlickityClass(carousel, {
      cellSelector: '.stl-card',
      cellAlign: 'left',
      contain: true,
      wrapAround: false,
      pageDots: false,
      prevNextButtons: false,
      draggable: '>1',
      freeScroll: false,
      percentPosition: true,
      rightToLeft: window.themeVariables && window.themeVariables.settings.direction === 'rtl'
    });

    // Prevent click (modal open) after drag
    var isDragging = false;
    flkty.on('dragStart', function () { isDragging = true; });
    flkty.on('dragEnd', function () {
      setTimeout(function () { isDragging = false; }, 100);
    });
    carousel.addEventListener('click', function (e) {
      if (isDragging) {
        e.stopPropagation();
        e.preventDefault();
      }
    }, true);

    // Wire up prev/next buttons
    var prevBtn = container.querySelector('[data-stl-prev]');
    var nextBtn = container.querySelector('[data-stl-next]');

    if (prevBtn && nextBtn) {
      // Find next/prev slide with a different target position (skip contain-grouped slides)
      function goNext() {
        var slides = flkty.slides;
        var idx = flkty.selectedIndex;
        var currentTarget = slides[idx].target;
        for (var i = idx + 1; i < slides.length; i++) {
          if (slides[i].target !== currentTarget) {
            flkty.select(i);
            return;
          }
        }
      }

      function goPrev() {
        var slides = flkty.slides;
        var idx = flkty.selectedIndex;
        var currentTarget = slides[idx].target;
        for (var i = idx - 1; i >= 0; i--) {
          if (slides[i].target !== currentTarget) {
            flkty.select(i);
            return;
          }
        }
        // If all previous slides share the same target, go to 0
        if (idx > 0) flkty.select(0);
      }

      prevBtn.addEventListener('click', function () { goPrev(); });
      nextBtn.addEventListener('click', function () { goNext(); });

      function updateArrows() {
        var idx = flkty.selectedIndex;
        var slides = flkty.slides;
        var isAtStart = idx === 0;
        var isAtEnd = idx === slides.length - 1;

        // With contain:true, slides beyond the visual end share the same target position
        if (!isAtEnd && slides[idx] && slides[slides.length - 1]) {
          isAtEnd = slides[idx].target === slides[slides.length - 1].target;
        }
        if (!isAtStart && slides[idx] && slides[0]) {
          isAtStart = slides[idx].target === slides[0].target;
        }

        prevBtn.classList.toggle('is-disabled', isAtStart);
        nextBtn.classList.toggle('is-disabled', isAtEnd);
      }

      flkty.on('change', updateArrows);

      // Initial state
      updateArrows();

      // Hide nav entirely if no scrolling needed
      var nav = container.querySelector('.stl-slider__nav');
      if (nav && isAtStartAndEnd()) nav.style.display = 'none';

      function isAtStartAndEnd() {
        var slides = flkty.slides;
        return slides.length <= 1 || slides[0].target === slides[slides.length - 1].target;
      }
    }
  }

  // ─── 6. Bootstrap ─────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
